import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  GetApp as DownloadIcon,
  PhotoCamera as PhotoIcon,
  Description as DocumentIcon,
  Assignment as PassportIcon,
  FilterList as FilterIcon,
  FolderOpen as FolderIcon,
  Archive as ZipIcon,
  UploadFile as FileIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
} from '@mui/icons-material';
import AdminLayout from '../components/layout/AdminLayout';
import { toursAPI, apiCall } from '../services/api';
import uploadTracker from '../services/uploadTracker';

const BulkDocumentUpload = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Main state
  const [tours, setTours] = useState([]);
  const [selectedTour, setSelectedTour] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  // Upload method state
  const [uploadMethod, setUploadMethod] = useState(0); // 0: Files, 1: ZIP, 2: Folder

  // Files upload state
  const [uploadProgress, setUploadProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [batchSize, setBatchSize] = useState(10);
  const [previewDialog, setPreviewDialog] = useState({ open: false, files: [] });

  // Folder upload state
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [tourMembers, setTourMembers] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [folderUploadProgress, setFolderUploadProgress] = useState({
    active: false,
    paused: false,
    currentBatch: 0,
    totalBatches: 0,
    totalFiles: 0,
    processed: 0,
    successful: 0,
    failed: 0,
    skipped: 0
  });
  const [folderUploadResults, setFolderUploadResults] = useState([]);
  const [showResults, setShowResults] = useState(false);

  // Other state
  const [missingDocuments, setMissingDocuments] = useState([]);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Download state
  const [downloading, setDownloading] = useState(false);
  const [downloadType, setDownloadType] = useState('');

  // Check if File System Access API is supported
  const isFileSystemSupported = 'showDirectoryPicker' in window;

  // Ref to track upload control state
  const uploadControlRef = useRef({ shouldStop: false, shouldPause: false });

  const documentTypes = [
    {
      key: 'passport',
      label: 'Passport Copies',
      icon: PassportIcon,
      description: 'Files mapped by ID Card field in filename (e.g., "61b1733080fc5_passportCopy_A162305")',
      accept: '.pdf,.jpg,.jpeg,.png'
    },
    {
      key: 'visa',
      label: 'Visa Documents',
      icon: DocumentIcon,
      description: 'PDF or image files matched by reading passport numbers from document content using OCR. Filenames do NOT matter - matching is done automatically by scanning the document.',
      accept: '.pdf,.jpg,.jpeg,.png'
    },
    {
      key: 'profile_photo',
      label: 'Profile Photos',
      icon: PhotoIcon,
      description: 'Image files mapped by ID Card field in filename (exact match required)',
      accept: '.jpg,.jpeg,.png,.gif'
    },
  ];

  useEffect(() => {
    fetchTours();
  }, []);

  useEffect(() => {
    if (selectedTour && documentType) {
      fetchMissingDocuments();
    }
  }, [selectedTour, documentType]);

  useEffect(() => {
    if (selectedTour) {
      fetchUploadHistory();
      // Fetch tour members for both Individual Files and Folder Upload methods
      if (uploadMethod === 0 || uploadMethod === 2) {
        fetchTourMembers(selectedTour);
      }
    }
  }, [selectedTour, uploadMethod]);

  const fetchTours = async () => {
    try {
      const data = await apiCall(toursAPI.getAll);
      setTours(data.tours);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchTourMembers = async (tourId) => {
    try {
      const response = await fetch(`/api/travelers?tour_id=${tourId}&limit=5000`, {
        credentials: 'include'
      });
      const data = await response.json();
      setTourMembers(data.travelers || []);
      console.log(`Loaded ${data.travelers?.length || 0} tour members for matching`);
    } catch (error) {
      console.error('Error fetching tour members:', error);
    }
  };

  const fetchMissingDocuments = async () => {
    try {
      const response = await fetch(`/api/bulk-documents/missing?tour_id=${selectedTour}&document_type=${documentType}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (response.ok) {
        setMissingDocuments(data.missing_travelers);
      } else {
        throw new Error(data.error);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchUploadHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await fetch(`/api/bulk-documents/history?tour_id=${selectedTour}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (response.ok) {
        setUploadHistory(data.history);
      } else {
        throw new Error(data.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingHistory(false);
    }
  };

  // File selection for individual files - optimized for large file counts
  const handleFileSelect = (event) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;

    console.log(`📁 Selected ${fileList.length} files`);

    // Show immediate feedback
    if (fileList.length > 100) {
      console.log('⏳ Processing large file selection, please wait...');
    }

    // For large file selections (>100), process asynchronously to prevent UI freeze
    if (fileList.length > 100) {
      // Use setTimeout to allow UI to update first
      setTimeout(() => {
        processLargeFileSelection(fileList);
      }, 100);
    } else {
      // For smaller selections, process immediately and filter
      const files = Array.from(fileList);

      // Filter files by tour members (only upload matched files)
      // SKIP FILTERING FOR VISA DOCUMENTS - they are matched by OCR on backend
      if (tourMembers.length > 0 && documentType !== 'visa') {
        const matchedFiles = filterFilesByTourMembers(files, tourMembers);
        console.log(`✅ Matched ${matchedFiles.length} of ${files.length} files`);

        if (matchedFiles.length === 0) {
          alert(`None of the ${files.length} selected files match any tour member ID cards.`);
          return;
        }

        if (matchedFiles.length < files.length) {
          const proceed = window.confirm(
            `${matchedFiles.length} of ${files.length} files match tour members. Continue?`
          );
          if (!proceed) return;
        }

        setPreviewDialog({ open: true, files: matchedFiles });
      } else {
        // For visa documents or when no tour members loaded, send all files to backend
        setPreviewDialog({ open: true, files });
      }
    }
  };

  // Process large file selections without freezing the UI
  const processLargeFileSelection = (fileList) => {
    console.log(`🔄 Processing ${fileList.length} files...`);

    // Convert FileList to array in chunks to prevent freeze
    const files = [];
    const chunkSize = 500;

    for (let i = 0; i < fileList.length; i += chunkSize) {
      const chunk = Array.from(fileList).slice(i, Math.min(i + chunkSize, fileList.length));
      files.push(...chunk);
      console.log(`  Processed ${Math.min(i + chunkSize, fileList.length)}/${fileList.length} files`);
    }

    console.log(`✅ Finished processing ${files.length} files`);

    // Filter files by tour members (only upload matched files)
    // SKIP FILTERING FOR VISA DOCUMENTS - they are matched by OCR on backend
    if (tourMembers.length > 0 && documentType !== 'visa') {
      console.log(`🔍 Filtering ${files.length} files against ${tourMembers.length} tour members...`);
      const matchedFiles = filterFilesByTourMembers(files, tourMembers);
      console.log(`✅ Matched ${matchedFiles.length} files (${Math.round(matchedFiles.length/files.length*100)}% match rate)`);

      if (matchedFiles.length === 0) {
        alert(`None of the ${files.length} selected files match any tour member ID cards. Please check your file naming.`);
        return;
      }

      const unmatchedCount = files.length - matchedFiles.length;
      if (unmatchedCount > 0) {
        const proceed = window.confirm(
          `Found ${matchedFiles.length} matching files out of ${files.length} selected.\n\n` +
          `${unmatchedCount} files don't match any tour members and will be skipped.\n\n` +
          `Continue with uploading ${matchedFiles.length} matched files?`
        );
        if (!proceed) {
          return;
        }
      }

      setPreviewDialog({ open: true, files: matchedFiles });
    } else {
      // For visa documents or when no tour members loaded, send all files to backend
      console.log(`📤 Sending all ${files.length} files to backend for processing (${documentType === 'visa' ? 'OCR matching' : 'no filtering'})`);
      setPreviewDialog({ open: true, files });
    }
  };

  // Folder selection for folder upload
  const selectFolder = async () => {
    if (!isFileSystemSupported) {
      alert('Your browser does not support folder selection. Please use Chrome or Edge.');
      return;
    }

    try {
      const dirHandle = await window.showDirectoryPicker();
      setSelectedFolder(dirHandle);

      // Get only filenames (like 'dir' command) without loading file contents
      const fileEntries = await getFileEntriesFromDirectory(dirHandle);
      console.log(`Found ${fileEntries.length} files in folder`);

      // Filter locally by filename only - no file loading!
      // SKIP FILTERING FOR VISA DOCUMENTS - they are matched by OCR on backend
      if (documentType === 'visa') {
        console.log(`📤 Visa documents: Sending all ${fileEntries.length} files to backend for OCR matching`);
        setFilteredFiles(fileEntries);
      } else {
        const matchedEntries = filterFileEntriesByTourMembers(fileEntries);
        console.log(`Matched ${matchedEntries.length} files to tour members (${Math.round(matchedEntries.length/fileEntries.length*100)}%)`);
        setFilteredFiles(matchedEntries);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error selecting folder:', error);
        alert('Error selecting folder. Please try again.');
      }
    }
  };

  // Get file entries without loading file contents (like 'dir' command)
  const getFileEntriesFromDirectory = async (dirHandle) => {
    const fileEntries = [];
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.gif'];

    for await (const entry of dirHandle.values()) {
      if (entry.kind === 'file') {
        const extension = entry.name.toLowerCase().substr(entry.name.lastIndexOf('.'));

        if (allowedExtensions.includes(extension)) {
          // Store only filename and handle - no file loading!
          fileEntries.push({
            name: entry.name,
            handle: entry,
            extension: extension
          });
        }
      }
    }

    return fileEntries;
  };

  // Filter file entries by filename matching (no file loading) - use individual upload logic
  const filterFileEntriesByTourMembers = (fileEntries) => {
    if (tourMembers.length === 0) return [];

    // Get array of valid ID cards (same as individual upload method)
    const validIdCards = tourMembers.map(t => t.id_card?.toUpperCase()).filter(Boolean);

    console.log('🔍 Valid ID Cards to match:', validIdCards);

    return fileEntries.filter(entry => {
      const filename = entry.name.toUpperCase();

      // Check if filename contains any VALID ID card (same as individual upload)
      for (const idCard of validIdCards) {
        if (filename.includes(idCard)) {
          console.log(`✅ Matched file: ${entry.name} (contains ID: ${idCard})`);
          return true;
        }
      }

      return false;
    });
  };

  const filterFilesByTourMembers = (files, members) => {
    const idCardSet = new Set(members.map(m => m.id_card?.toUpperCase()).filter(Boolean));

    return files.filter(file => {
      const filename = file.name.toUpperCase();

      // ONLY match exact ID cards found in filename
      const idCardMatch = filename.match(/[A-Z]\d{6}/g);
      if (idCardMatch?.some(id => idCardSet.has(id))) {
        return true;
      }

      return false;
    });
  };

  // Start folder batch upload
  const startFolderBatchUpload = async () => {
    if (!selectedTour || !documentType || filteredFiles.length === 0) {
      alert('Please select tour, document type, and folder with files');
      return;
    }

    const batchSize = 8;
    const batches = [];

    for (let i = 0; i < filteredFiles.length; i += batchSize) {
      batches.push(filteredFiles.slice(i, i + batchSize));
    }

    // Reset control state
    uploadControlRef.current = { shouldStop: false, shouldPause: false };

    setFolderUploadProgress({
      active: true,
      paused: false,
      currentBatch: 0,
      totalBatches: batches.length,
      totalFiles: filteredFiles.length,
      processed: 0,
      successful: 0,
      failed: 0,
      skipped: 0
    });

    setFolderUploadResults([]);

    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      // Check if upload should be stopped
      if (uploadControlRef.current.shouldStop) {
        console.log('Upload stopped by user');
        break;
      }

      // Check if upload should be paused
      while (uploadControlRef.current.shouldPause && !uploadControlRef.current.shouldStop) {
        console.log('Upload paused, waiting...');
        await new Promise(resolve => setTimeout(resolve, 1000)); // Check every second
      }

      // Check again if stopped while paused
      if (uploadControlRef.current.shouldStop) {
        console.log('Upload stopped while paused');
        break;
      }

      const batch = batches[batchIndex];

      setFolderUploadProgress(prev => ({
        ...prev,
        currentBatch: batchIndex + 1
      }));

      try {
        const result = await uploadFolderBatch(batch, batchIndex + 1);

        setFolderUploadProgress(prev => ({
          ...prev,
          processed: prev.processed + batch.length,
          successful: prev.successful + result.successful,
          failed: prev.failed + result.failed,
          skipped: prev.skipped + result.skipped
        }));

        setFolderUploadResults(prev => [...prev, result]);

        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }

      } catch (error) {
        console.error(`Batch ${batchIndex + 1} failed:`, error);
        setFolderUploadProgress(prev => ({
          ...prev,
          processed: prev.processed + batch.length,
          failed: prev.failed + batch.length
        }));
      }
    }

    setFolderUploadProgress(prev => ({ ...prev, active: false }));
    setShowResults(true);
  };

  // Pause upload
  const pauseFolderUpload = () => {
    uploadControlRef.current.shouldPause = true;
    setFolderUploadProgress(prev => ({ ...prev, paused: true }));
    console.log('Pause requested');
  };

  // Resume upload
  const resumeFolderUpload = () => {
    uploadControlRef.current.shouldPause = false;
    setFolderUploadProgress(prev => ({ ...prev, paused: false }));
    console.log('Resume requested');
  };

  // Stop upload
  const stopFolderUpload = () => {
    uploadControlRef.current.shouldStop = true;
    uploadControlRef.current.shouldPause = false;
    setFolderUploadProgress({
      active: false,
      paused: false,
      currentBatch: 0,
      totalBatches: 0,
      totalFiles: 0,
      processed: 0,
      successful: 0,
      failed: 0,
      skipped: 0
    });
    console.log('Stop requested');
  };

  const uploadFolderBatch = async (fileEntries, batchNumber) => {
    const formData = new FormData();

    // Load files only when uploading (not during filtering)
    for (const entry of fileEntries) {
      const file = await entry.handle.getFile();
      // Skip files over 10MB
      if (file.size <= 10 * 1024 * 1024) {
        formData.append('files', file);
      }
    }

    formData.append('document_type', documentType);
    formData.append('tour_id', selectedTour);
    formData.append('batch_number', batchNumber);

    const response = await fetch('/api/bulk-documents/folder-upload-batch', {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });

    if (!response.ok) {
      // Get error details from response
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
        console.error('Server error details:', errorData);
      } catch (e) {
        console.error('Failed to parse error response');
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    return {
      batch_number: batchNumber,
      successful: result.successful || 0,
      failed: result.failed || 0,
      skipped: result.skipped || 0,
      total: fileEntries.length,
      details: result
    };
  };

  // Handle bulk upload (files/ZIP)
  const handleBulkUpload = async (files) => {
    if (!selectedTour || !documentType) {
      setError('Please select a tour and document type');
      return;
    }

    if (uploadMethod === 1) {
      return handleZipUpload(files[0]);
    }

    const shouldBatch = files.length > batchSize;

    if (shouldBatch) {
      return handleBatchUpload(files);
    }

    return handleDirectUpload(files);
  };

  const handleDirectUpload = async (files) => {
    try {
      setUploading(true);
      setUploadProgress(0);
      setError(null);
      setResults(null);

      const uploadId = uploadTracker.generateUploadId();
      uploadTracker.startUpload(uploadId, {
        totalFiles: files.length,
        documentType,
        tourId: selectedTour,
        uploadMode: 'files'
      });

      console.log(`🚀 Starting upload of ${files.length} files for ${documentType}`);

      const formData = new FormData();
      files.forEach(file => {
        formData.append('documents', file);
      });
      formData.append('document_type', documentType);
      formData.append('tour_id', selectedTour);

      const totalSize = Array.from(files).reduce((sum, file) => sum + file.size, 0);
      console.log(`📦 FormData created - ${files.length} files, total size: ${(totalSize / 1024 / 1024).toFixed(2)} MB`);

      let progressValue = 0;
      const progressInterval = setInterval(() => {
        if (progressValue < 85) {
          progressValue += 3;
          setUploadProgress(progressValue);
          uploadTracker.updateProgress(progressValue);
        }
      }, 800);

      console.log(`🌐 Sending request to /api/bulk-documents/upload...`);
      const response = await fetch('/api/bulk-documents/upload', {
        method: 'POST',
        credentials: 'include',
        body: formData,
        signal: AbortSignal.timeout(30 * 60 * 1000)
      });
      console.log(`📡 Response received: ${response.status} ${response.statusText}`);

      clearInterval(progressInterval);
      setUploadProgress(100);
      uploadTracker.updateProgress(100);

      const data = await response.json();

      if (response.ok) {
        setResults(data.results);
        uploadTracker.completeUpload(data.results);
        await fetchMissingDocuments();
        await fetchUploadHistory();
      } else {
        const error = data.error || 'Upload failed';
        uploadTracker.failUpload(new Error(error));
        throw new Error(error);
      }

    } catch (err) {
      console.error(`❌ Upload error:`, err);
      if (err.name === 'TimeoutError') {
        const error = 'Upload timed out. Please try with fewer files or check your connection.';
        console.error(`⏰ Timeout error: ${error}`);
        setError(error);
        uploadTracker.failUpload(new Error(error));
      } else {
        setError(err.message);
        uploadTracker.failUpload(err);
      }
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 2000);
    }
  };

  const handleBatchUpload = async (files) => {
    try {
      setUploading(true);
      setUploadProgress(0);
      setError(null);
      setResults(null);

      console.log(`🎯 Starting batch upload of ${files.length} files with batch size ${batchSize}`);

      // Create batches based on user-selected batch size
      const batches = [];
      for (let i = 0; i < files.length; i += batchSize) {
        batches.push(files.slice(i, i + batchSize));
      }

      console.log(`📦 Created ${batches.length} batches`);

      // Initialize cumulative results
      const cumulativeResults = {
        successful: [],
        failed: [],
        duplicates: [],
        processed: 0,
        total: files.length
      };

      const uploadId = uploadTracker.generateUploadId();
      uploadTracker.startUpload(uploadId, {
        totalFiles: files.length,
        documentType,
        tourId: selectedTour,
        uploadMode: 'batch'
      });

      // Process each batch sequentially
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        const batchNumber = batchIndex + 1;

        console.log(`🚀 Processing batch ${batchNumber}/${batches.length} (${batch.length} files)`);

        try {
          const formData = new FormData();
          batch.forEach(file => {
            formData.append('documents', file);
          });
          formData.append('document_type', documentType);
          formData.append('tour_id', selectedTour);

          const response = await fetch('/api/bulk-documents/upload', {
            method: 'POST',
            credentials: 'include',
            body: formData,
            signal: AbortSignal.timeout(10 * 60 * 1000) // 10 minutes per batch
          });

          const data = await response.json();

          if (response.ok && data.results) {
            // Accumulate results from this batch
            cumulativeResults.successful.push(...(data.results.successful || []));
            cumulativeResults.failed.push(...(data.results.failed || []));
            cumulativeResults.duplicates.push(...(data.results.duplicates || []));
            cumulativeResults.processed += batch.length;

            console.log(`✅ Batch ${batchNumber} completed: ${data.results.successful?.length || 0} successful, ${data.results.failed?.length || 0} failed`);
          } else {
            throw new Error(data.error || `Batch ${batchNumber} failed`);
          }

          // Update progress
          const progressPercent = Math.round((cumulativeResults.processed / files.length) * 100);
          setUploadProgress(progressPercent);
          uploadTracker.updateProgress(progressPercent);

          // Add delay between batches to prevent server overload
          if (batchIndex < batches.length - 1) {
            console.log(`⏱️ Waiting 2 seconds before next batch...`);
            await new Promise(resolve => setTimeout(resolve, 2000));
          }

        } catch (batchError) {
          console.error(`❌ Batch ${batchNumber} failed:`, batchError);

          // Add failed files to results
          const failedFiles = batch.map(file => ({
            filename: file.name,
            error: `Batch ${batchNumber} failed: ${batchError.message}`
          }));
          cumulativeResults.failed.push(...failedFiles);
          cumulativeResults.processed += batch.length;

          // Continue with next batch instead of stopping
        }
      }

      console.log(`🎉 Batch upload completed: ${cumulativeResults.successful.length} successful, ${cumulativeResults.failed.length} failed, ${cumulativeResults.duplicates.length} duplicates`);

      setResults(cumulativeResults);
      setUploadProgress(100);
      uploadTracker.completeUpload();

      // Refresh the missing documents and history
      await fetchMissingDocuments();
      await fetchUploadHistory();

    } catch (error) {
      console.error('Batch upload error:', error);
      setError(error.message);
      uploadTracker.failUpload(error);
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 3000);
    }
  };

  const handleZipUpload = async (zipFile) => {
    try {
      setUploading(true);
      setUploadProgress(0);
      setError(null);
      setResults(null);

      const formData = new FormData();
      formData.append('zipFile', zipFile);
      formData.append('document_type', documentType);
      formData.append('tour_id', selectedTour);

      let progressValue = 0;
      const progressInterval = setInterval(() => {
        if (progressValue < 90) {
          progressValue += 2;
          setUploadProgress(progressValue);
        }
      }, 1000);

      const response = await fetch('/api/bulk-documents/upload-zip', {
        method: 'POST',
        credentials: 'include',
        body: formData,
        signal: AbortSignal.timeout(45 * 60 * 1000)
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      const data = await response.json();

      if (response.ok) {
        setResults(data.results);
        await fetchMissingDocuments();
        await fetchUploadHistory();
      } else {
        throw new Error(data.error || 'ZIP upload failed');
      }

    } catch (err) {
      if (err.name === 'TimeoutError') {
        setError('ZIP upload timed out. The archive was too large or processing took too long.');
      } else {
        setError(err.message);
      }
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 2000);
    }
  };

  const handleQRCodeDownload = async () => {
    if (!selectedTour) {
      setError('Please select a tour');
      return;
    }

    try {
      setDownloading(true);
      setDownloadType('QR Codes');
      setError(null);

      const response = await fetch('/api/bulk-documents/qr-codes/download', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tour_id: selectedTour })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `qr-codes-${selectedTour}-${Date.now()}.zip`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const data = await response.json();
        throw new Error(data.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(false);
      setDownloadType('');
    }
  };

  const handleOriginalsDownload = async (types = null) => {
    if (!selectedTour) {
      setError('Please select a tour');
      return;
    }

    try {
      // Set download type for display
      let displayType = 'All Documents';
      if (types && types.length > 0) {
        const typeNames = {
          'profile_photo': 'PP Photos',
          'passport': 'Passports',
          'visa': 'Visas'
        };
        displayType = types.map(t => typeNames[t] || t).join(', ');
      }

      setDownloading(true);
      setDownloadType(displayType);
      setError(null);

      const body = {
        tour_id: selectedTour
      };

      if (types) {
        body.document_types = types;
      }

      const response = await fetch('/api/bulk-documents/originals/download', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;

        // Generate appropriate filename
        const typeLabel = types ? types.join('-') : 'all-documents';
        a.download = `${typeLabel}-tour-${selectedTour}-${Date.now()}.zip`;

        a.click();
        URL.revokeObjectURL(url);
      } else {
        const data = await response.json();
        throw new Error(data.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(false);
      setDownloadType('');
    }
  };

  const getDocumentTypeConfig = (type) => {
    return documentTypes.find(dt => dt.key === type);
  };

  const documentTypeConfig = getDocumentTypeConfig(documentType);

  const TabPanel = ({ children, value, index, ...other }) => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`upload-tabpanel-${index}`}
      aria-labelledby={`upload-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <AdminLayout>
      <Box>
        {/* Header */}
        <Box mb={4}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            Bulk Document Upload
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Upload multiple documents with automatic traveler mapping
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {downloading && (
          <Alert severity="info" sx={{ mb: 3 }} icon={<CircularProgress size={20} />}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Preparing {downloadType} for download...
            </Typography>
            <Typography variant="caption" color="text.secondary">
              This may take a few moments. Please wait while we compile your files.
            </Typography>
            <LinearProgress sx={{ mt: 1 }} />
          </Alert>
        )}

        {/* Configuration Section */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Upload Configuration
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Select Tour</InputLabel>
                <Select
                  value={selectedTour}
                  label="Select Tour"
                  onChange={(e) => setSelectedTour(e.target.value)}
                >
                  {tours.map((tour) => (
                    <MenuItem key={tour.id} value={tour.id}>
                      {tour.name} ({tour.traveler_count || 0} travelers)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={documentType}
                  label="Document Type"
                  onChange={(e) => setDocumentType(e.target.value)}
                >
                  {documentTypes.map((type) => (
                    <MenuItem key={type.key} value={type.key}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <type.icon fontSize="small" />
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {documentTypeConfig && (
            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {documentTypeConfig.label} Mapping Rules:
              </Typography>
              <Typography variant="body2">
                {documentTypeConfig.description}
              </Typography>
            </Alert>
          )}

          {/* Actions */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Bulk Downloads
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={downloading && downloadType === 'QR Codes' ? <CircularProgress size={20} /> : <DownloadIcon />}
                onClick={handleQRCodeDownload}
                disabled={!selectedTour || downloading}
              >
                QR Codes
              </Button>
              <Button
                variant="contained"
                startIcon={downloading && downloadType === 'All Documents' ? <CircularProgress size={20} /> : <DownloadIcon />}
                onClick={() => handleOriginalsDownload()}
                disabled={!selectedTour || downloading}
                color="primary"
              >
                All Documents
              </Button>
              <Button
                variant="outlined"
                startIcon={downloading && downloadType === 'PP Photos' ? <CircularProgress size={20} /> : <PhotoIcon />}
                onClick={() => handleOriginalsDownload(['profile_photo'])}
                disabled={!selectedTour || downloading}
                color="secondary"
              >
                PP Photos
              </Button>
              <Button
                variant="outlined"
                startIcon={downloading && downloadType === 'Passports' ? <CircularProgress size={20} /> : <PassportIcon />}
                onClick={() => handleOriginalsDownload(['passport'])}
                disabled={!selectedTour || downloading}
                color="secondary"
              >
                Passports
              </Button>
              <Button
                variant="outlined"
                startIcon={downloading && downloadType === 'Visas' ? <CircularProgress size={20} /> : <DocumentIcon />}
                onClick={() => handleOriginalsDownload(['visa'])}
                disabled={!selectedTour || downloading}
                color="secondary"
              >
                Visas
              </Button>
            </Box>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Download documents in their original format (JPEG as JPEG, PDF as PDF).
                Files are named with UUID and ID card name for easy identification.
              </Typography>
            </Alert>
          </Box>
        </Paper>

        {/* Missing Documents Section */}
        {selectedTour && documentType && missingDocuments.length > 0 && (
          <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon color="warning" />
              Travelers Missing {documentTypes.find(dt => dt.key === documentType)?.label}
              <Chip
                label={missingDocuments.length}
                color="warning"
                size="small"
              />
            </Typography>

            <Alert severity="warning" sx={{ mb: 3 }}>
              The following travelers are missing {documentTypes.find(dt => dt.key === documentType)?.label.toLowerCase()}.
              Files uploaded will be automatically matched to these travelers when found.
            </Alert>

            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Traveler Name</TableCell>
                      <TableCell>ID Card</TableCell>
                      <TableCell>Passport No</TableCell>
                      <TableCell>Hajji No</TableCell>
                      <TableCell>Group</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {missingDocuments.map((traveler) => (
                      <TableRow key={traveler.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {traveler.full_name || traveler.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {traveler.id_card || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {traveler.passport_number || traveler.passport_no || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {traveler.hajji_no || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {traveler.group_name || traveler.group || 'N/A'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {missingDocuments.length > 9 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                {missingDocuments.length > 12 ? `Showing first 12 of ${missingDocuments.length} travelers` : `${missingDocuments.length} travelers missing documents`}
              </Typography>
            )}
          </Paper>
        )}

        {/* Upload Methods Section */}
        {selectedTour && documentType && (
          <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Upload Methods
            </Typography>

            <Tabs
              value={uploadMethod}
              onChange={(e, newValue) => setUploadMethod(newValue)}
              variant={isMobile ? "fullWidth" : "standard"}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab
                icon={<FileIcon />}
                label="Individual Files"
                iconPosition="start"
                sx={{ minHeight: 'auto', py: 2 }}
              />
              <Tab
                icon={<ZipIcon />}
                label="ZIP Archive"
                iconPosition="start"
                sx={{ minHeight: 'auto', py: 2 }}
              />
              <Tab
                icon={<FolderIcon />}
                label="Folder Upload"
                iconPosition="start"
                sx={{ minHeight: 'auto', py: 2 }}
                disabled={!isFileSystemSupported}
              />
            </Tabs>

            {/* Individual Files Tab */}
            <TabPanel value={uploadMethod} index={0}>
              <Stack spacing={3}>
                <Alert severity="info">
                  <strong>Individual Files:</strong> Select multiple files directly.
                  Files over {batchSize} will be processed in batches with delays to prevent server overload.
                </Alert>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Batch Size</InputLabel>
                    <Select
                      value={batchSize}
                      onChange={(e) => setBatchSize(Number(e.target.value))}
                      label="Batch Size"
                    >
                      <MenuItem value={10}>10 files per batch</MenuItem>
                      <MenuItem value={25}>25 files per batch</MenuItem>
                      <MenuItem value={50}>50 files per batch</MenuItem>
                      <MenuItem value={100}>100 files per batch</MenuItem>
                    </Select>
                  </FormControl>

                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<UploadIcon />}
                    disabled={uploading}
                    size="large"
                  >
                    Select Files
                    <input
                      type="file"
                      hidden
                      multiple
                      accept={documentTypeConfig?.accept}
                      onChange={handleFileSelect}
                    />
                  </Button>
                </Box>
              </Stack>
            </TabPanel>

            {/* ZIP Archive Tab */}
            <TabPanel value={uploadMethod} index={1}>
              <Stack spacing={3}>
                <Alert severity="info">
                  <strong>ZIP Archive:</strong> Upload a single ZIP file containing all documents.
                  The server will extract and map files automatically. Recommended for 1000+ files.
                </Alert>

                <Box>
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<ZipIcon />}
                    disabled={uploading}
                    size="large"
                  >
                    Select ZIP File
                    <input
                      type="file"
                      hidden
                      accept=".zip"
                      onChange={handleFileSelect}
                    />
                  </Button>
                </Box>
              </Stack>
            </TabPanel>

            {/* Folder Upload Tab */}
            <TabPanel value={uploadMethod} index={2}>
              <Stack spacing={3}>
                {!isFileSystemSupported && (
                  <Alert severity="warning">
                    Your browser doesn't support folder selection. Please use Chrome or Edge for this feature.
                  </Alert>
                )}

                <Alert severity="info">
                  <strong>Folder Upload:</strong> Select a folder containing documents. Files will be automatically
                  filtered to match tour members and uploaded in small batches without rate limiting.
                  Perfect for 3000+ files.
                </Alert>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          <FolderIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                          Folder Selection
                        </Typography>

                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={selectFolder}
                          startIcon={<FolderIcon />}
                          disabled={!selectedTour || folderUploadProgress.active || !isFileSystemSupported}
                          sx={{ mb: 2 }}
                        >
                          {selectedFolder ? 'Change Folder' : 'Select Folder'}
                        </Button>

                        {selectedFolder && (
                          <Alert severity="success" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                              <strong>Folder:</strong> {selectedFolder.name}<br/>
                              <strong>Matched files:</strong> {filteredFiles.length}<br/>
                              <strong>Tour members:</strong> {tourMembers.length}<br/>
                              <strong>✅ Optimized:</strong> Pre-filtered locally (no rate limiting)
                            </Typography>
                          </Alert>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Upload Progress
                        </Typography>

                        {folderUploadProgress.active && (
                          <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              Batch {folderUploadProgress.currentBatch} of {folderUploadProgress.totalBatches}
                              {folderUploadProgress.paused && (
                                <Chip label="PAUSED" color="warning" size="small" sx={{ ml: 1 }} />
                              )}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={(folderUploadProgress.processed / folderUploadProgress.totalFiles) * 100}
                              sx={{ mb: 2 }}
                            />
                            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                              <Chip label={`${folderUploadProgress.successful} Success`} color="success" size="small" />
                              <Chip label={`${folderUploadProgress.failed} Failed`} color="error" size="small" />
                              <Chip label={`${folderUploadProgress.skipped} Skipped`} color="warning" size="small" />
                            </Box>
                          </Box>
                        )}

                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Button
                            variant="contained"
                            onClick={startFolderBatchUpload}
                            disabled={folderUploadProgress.active || filteredFiles.length === 0}
                            startIcon={<PlayIcon />}
                          >
                            Start Upload
                          </Button>

                          {folderUploadProgress.active && (
                            <>
                              {!folderUploadProgress.paused ? (
                                <Button
                                  variant="outlined"
                                  startIcon={<PauseIcon />}
                                  onClick={pauseFolderUpload}
                                  size="small"
                                >
                                  Pause
                                </Button>
                              ) : (
                                <Button
                                  variant="outlined"
                                  startIcon={<PlayIcon />}
                                  onClick={resumeFolderUpload}
                                  size="small"
                                >
                                  Resume
                                </Button>
                              )}
                              <Button
                                variant="outlined"
                                startIcon={<StopIcon />}
                                onClick={stopFolderUpload}
                                color="error"
                                size="small"
                              >
                                Stop
                              </Button>
                            </>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* File Preview for Folder Upload */}
                {filteredFiles.length > 0 && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Matched Files Ready for Upload ({filteredFiles.length})
                      </Typography>
                      <Alert severity="success" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          ✅ <strong>Optimized Workflow:</strong> Only matched files will be uploaded (no rate limiting issues)
                        </Typography>
                      </Alert>
                      <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                        <List dense>
                          {filteredFiles.slice(0, 20).map((entry, index) => (
                            <ListItem key={index}>
                              <ListItemIcon>
                                <SuccessIcon color="success" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary={entry.name}
                                secondary={`File type: ${entry.extension} • Matched to tour member`}
                              />
                            </ListItem>
                          ))}
                          {filteredFiles.length > 20 && (
                            <ListItem>
                              <ListItemText
                                primary={`... and ${filteredFiles.length - 20} more matched files`}
                                sx={{ fontStyle: 'italic' }}
                              />
                            </ListItem>
                          )}
                        </List>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Stack>
            </TabPanel>

            {/* Upload Progress Indicator */}
            {uploading && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Processing files... {uploadProgress}%
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}
          </Paper>
        )}

        {/* Missing Documents Alert */}
        {missingDocuments.length > 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {missingDocuments.length} travelers still need {documentTypeConfig?.label.toLowerCase()}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Upload files for these travelers to complete their documentation.
            </Typography>
          </Alert>
        )}

        {/* Results Section */}
        {results && (
          <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Upload Results
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Card sx={{ backgroundColor: 'success.light', color: 'success.contrastText' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <SuccessIcon sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h4">{results.successful?.length || results.successful || 0}</Typography>
                    <Typography variant="body2">Successful</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ backgroundColor: 'error.light', color: 'error.contrastText' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <ErrorIcon sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h4">{results.failed?.length || results.failed || 0}</Typography>
                    <Typography variant="body2">Failed</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ backgroundColor: 'warning.light', color: 'warning.contrastText' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <WarningIcon sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h4">{results.duplicates?.length || 0}</Typography>
                    <Typography variant="body2">Duplicates</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Upload History */}
        {selectedTour && uploadHistory.length > 0 && (
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Recent Upload History
            </Typography>

            {loadingHistory ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Document Type</TableCell>
                      <TableCell align="right">Documents</TableCell>
                      <TableCell align="right">Travelers</TableCell>
                      <TableCell>Uploaded By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uploadHistory.slice(0, 10).map((entry, index) => {
                      const documentTypeConfig = getDocumentTypeConfig(entry.document_type);
                      return (
                        <TableRow key={index}>
                          <TableCell>
                            {new Date(entry.upload_date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              {documentTypeConfig?.icon && <documentTypeConfig.icon fontSize="small" />}
                              {documentTypeConfig?.label || entry.document_type}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={entry.document_count}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={entry.traveler_count}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {entry.uploaded_by || 'Unknown'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {/* File Preview Dialog */}
        <Dialog
          open={previewDialog.open}
          onClose={() => setPreviewDialog({ open: false, files: [] })}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Confirm Upload - {previewDialog.files.length} files
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Review the files before uploading. Files will be automatically mapped to travelers.
            </Typography>

            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              <List>
                {previewDialog.files.slice(0, 10).map((file, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      {documentTypeConfig?.icon && <documentTypeConfig.icon />}
                    </ListItemIcon>
                    <ListItemText
                      primary={file.name}
                      secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                    />
                  </ListItem>
                ))}
                {previewDialog.files.length > 10 && (
                  <ListItem>
                    <ListItemText
                      primary={`... and ${previewDialog.files.length - 10} more files`}
                      sx={{ fontStyle: 'italic' }}
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewDialog({ open: false, files: [] })}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                handleBulkUpload(previewDialog.files);
                setPreviewDialog({ open: false, files: [] });
              }}
              disabled={uploading}
            >
              Upload {previewDialog.files.length} Files
            </Button>
          </DialogActions>
        </Dialog>

        {/* Folder Upload Results Dialog */}
        <Dialog open={showResults} onClose={() => setShowResults(false)} maxWidth="md" fullWidth>
          <DialogTitle>Folder Upload Results</DialogTitle>
          <DialogContent>
            <Typography variant="h6" gutterBottom>
              Summary: {folderUploadProgress.successful} successful, {folderUploadProgress.failed} failed, {folderUploadProgress.skipped} skipped
            </Typography>

            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {folderUploadResults.map((result, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={`Batch ${result.batch_number}: ${result.successful}/${result.total} successful`}
                    secondary={result.errors?.length > 0 ? `Errors: ${result.errors.map(e => e.filename).join(', ')}` : 'All files processed successfully'}
                  />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowResults(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default BulkDocumentUpload;